import React from 'react';
import { Spin } from 'antd';

import DashboardModal from './DashboardModal';
import {
    getHoverPropertyDetailsUrl,
    openHoverJobViewer,
} from '../../../../services/oneButtonServices/hoverServices';

import type { HoverJobModalPhase } from './useHoverJobAction';
import './oneButton-css/HoverJobCreatingModal.css';

export type HoverJobCreatingModalProps = {
    open: boolean;
    onClose: () => void;
    phase: HoverJobModalPhase;
    jobId: string | null;
    errorMessage?: string;
    theme: 'dark' | 'light';
    onConfirmCreate?: () => void;
};

/** Confirm, progress, and success modal for Hover job creation. */
export const HoverJobCreatingModal: React.FC<HoverJobCreatingModalProps> = ({
    open,
    onClose,
    phase,
    jobId,
    errorMessage,
    theme,
    onConfirmCreate,
}) => {
    const normalizedJobId = String(jobId ?? '').replace(/^#/, '').trim();
    const jobUrl = normalizedJobId ? getHoverPropertyDetailsUrl(normalizedJobId) : '';

    const handleOpenJob = () => {
        if (!normalizedJobId) return;
        openHoverJobViewer(normalizedJobId);
    };

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title="Hover Job"
            theme={theme}
            width={440}
            maskClosable={phase === 'confirm' || phase === 'success' || phase === 'error'}
            destroyOnClose
        >
            <div className="obd-hover-job-modal">
                {phase === 'confirm' ? (
                    <div className="obd-hover-job-modal__confirm">
                        <p className="obd-hover-job-modal__message">
                            Create a Hover job for this opportunity?
                        </p>
                        <div className="obd-hover-job-modal__actions">
                            <button type="button" className="obd-hover-job-modal__cancel-btn" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="obd-hover-job-modal__confirm-btn"
                                onClick={onConfirmCreate}
                            >
                                Create Hover Job
                            </button>
                        </div>
                    </div>
                ) : null}

                {phase === 'creating' ? (
                    <div className="obd-hover-job-modal__creating" role="status" aria-live="polite">
                        <Spin size="large" />
                        <p className="obd-hover-job-modal__message">Creating Hover Job...</p>
                    </div>
                ) : null}

                {phase === 'success' && normalizedJobId ? (
                    <div className="obd-hover-job-modal__success">
                        <p className="obd-hover-job-modal__message">Hover Job created successfully.</p>
                        <a
                            className="obd-hover-job-modal__link"
                            href={jobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => {
                                event.preventDefault();
                                handleOpenJob();
                            }}
                        >
                            Open Hover Job #{normalizedJobId}
                        </a>
                        <button type="button" className="obd-hover-job-modal__open-btn" onClick={handleOpenJob}>
                            Open in Hover
                        </button>
                    </div>
                ) : null}

                {phase === 'error' ? (
                    <div className="obd-hover-job-modal__error">
                        <p className="obd-hover-job-modal__message">
                            {errorMessage || 'Failed to create Hover Job.'}
                        </p>
                    </div>
                ) : null}
            </div>
        </DashboardModal>
    );
};

export default HoverJobCreatingModal;
